import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  enviarCorreoMasivo,
  obtenerHistorialCorreos,
  eliminarCorreo,
  clearSuccessMessage,
} from "../../redux/correosSlice";
import { Button, Modal, Form, Table, Alert, ButtonGroup, ProgressBar, Spinner } from "react-bootstrap";
import Swal from 'sweetalert2';

const CorreoMasivoPage = () => {
  const dispatch = useDispatch();
  const { correos, loading, error, successMessage } = useSelector((state) => state.correo);

  const [showModal, setShowModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState("");
  const [correoDetails, setCorreoDetails] = useState({
    asunto: "",
    mensaje: "",
    cc: "",
    bcc: "",
    adjuntos: [],
  });
  const [progress, setProgress] = useState(0);
  const [sending, setSending] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Efecto para manejar mensajes de éxito/error
  useEffect(() => {
    if (successMessage) {
      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: successMessage,
        confirmButtonText: 'Aceptar',
      }).then(() => {
        dispatch(clearSuccessMessage());
        dispatch(obtenerHistorialCorreos());
      });
    }

    if (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error,
        confirmButtonText: 'Aceptar',
      });
    }
  }, [successMessage, error, dispatch]);

  // Obtener historial al cargar
  useEffect(() => {
    dispatch(obtenerHistorialCorreos());
  }, [dispatch]);

  const handleCloseModal = () => {
    if (!sending) {
      setShowModal(false);
      setCorreoDetails({ asunto: "", mensaje: "", cc: "", bcc: "", adjuntos: [] });
      setValidationErrors({});
    }
  };

  const handleShowMessageModal = (mensaje) => {
    setSelectedMessage(mensaje);
    setShowMessageModal(true);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setSelectedMessage("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCorreoDetails({ ...correoDetails, [name]: value });
    validateField(name, value);
  };

  const handleFileUpload = (e) => {
    setCorreoDetails({ ...correoDetails, adjuntos: Array.from(e.target.files) });
  };

  const validateEmailList = (emails) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailList = emails.split(';');
    return emailList.filter(email => email.trim() && !emailRegex.test(email.trim()));
  };

  const validateField = (fieldName, value) => {
    let errors = { ...validationErrors };

    switch (fieldName) {
      case "asunto":
        if (!value) {
          errors.asunto = "El asunto es requerido";
        } else if (value.length > 100) {
          errors.asunto = "El asunto no puede tener más de 100 caracteres";
        } else {
          delete errors.asunto;
        }
        break;
      case "mensaje":
        if (!value) {
          errors.mensaje = "El mensaje es requerido";
        } else if (value.length < 10 || value.length > 10000) {
          errors.mensaje = "El mensaje debe tener entre 10 y 10,000 caracteres";
        } else {
          delete errors.mensaje;
        }
        break;
      case "cc":
        if (value) {
          const invalidEmails = validateEmailList(value);
          if (invalidEmails.length > 0) {
            errors.cc = `Correos inválidos: ${invalidEmails.join(', ')}. Use formato: correo1@sinterec.cl;correo2@sinterec.cl`;
          } else {
            delete errors.cc;
          }
        } else {
          delete errors.cc;
        }
        break;
      case "bcc":
        if (value) {
          const invalidEmails = validateEmailList(value);
          if (invalidEmails.length > 0) {
            errors.bcc = `Correos inválidos: ${invalidEmails.join(', ')}. Use formato: correo1@sinterec.cl;correo2@sinterec.cl`;
          } else {
            delete errors.bcc;
          }
        } else {
          delete errors.bcc;
        }
        break;
      default:
        break;
    }

    setValidationErrors(errors);
  };

  const handleSendCorreo = async () => {
    if (Object.keys(validationErrors).length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Por favor, corrige los errores antes de enviar el correo.',
      });
      return;
    }

    try {
      setSending(true);
      setProgress(0);

      const formData = new FormData();
      formData.append("asunto", correoDetails.asunto);
      formData.append("mensaje", correoDetails.mensaje);
      formData.append("cc", correoDetails.cc || "");
      formData.append("bcc", correoDetails.bcc || "");

      if (correoDetails.adjuntos.length > 0) {
        correoDetails.adjuntos.forEach(file => {
          formData.append("adjuntos", file);
        });
      }

      await dispatch(enviarCorreoMasivo(formData)).unwrap();
      
      // Simular progreso (en producción usaría el endpoint real)
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 5;
          if (newProgress >= 100) {
            clearInterval(interval);
            setSending(false);
            handleCloseModal();
            return 100;
          }
          return newProgress;
        });
      }, 300);

    } catch (err) {
      console.error("Error al enviar el correo:", err);
      setSending(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Correos Masivos</h1>
        <Button 
          onClick={() => setShowModal(true)} 
          variant="success"
          className="d-flex align-items-center"
        >
          <i className="bi bi-envelope-plus me-2"></i>
          <span className="d-none d-sm-inline">Enviar Correo Masivo</span>
        </Button>
      </div>

      {loading && !sending && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Cargando historial...</p>
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      <div className="d-block d-md-none alert alert-info mb-3">
        <i className="bi bi-phone me-1"></i>
        Desliza horizontalmente para ver todas las columnas
      </div>
      
      <div className="table-responsive">
        <Table striped bordered hover className="small">
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>Asunto</th>
              <th className="d-none d-md-table-cell">Mensaje</th>
              <th>Estado</th>
              <th className="text-nowrap">Fecha de Envío</th>
              <th className="text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {correos.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4 text-muted">
                  No hay correos registrados
                </td>
              </tr>
            ) : (
              correos.map((correo, index) => (
                <tr key={correo.id}>
                  <td>{index + 1}</td>
                  <td title={correo.asunto} className="text-truncate" style={{maxWidth: '150px'}}>
                    {correo.asunto}
                  </td>
                  <td 
                    title={correo.mensaje} 
                    className="d-none d-md-table-cell text-truncate" 
                    style={{maxWidth: '200px'}}
                  >
                    {correo.mensaje.length > 50 ? (
                      <>
                        {correo.mensaje.substring(0, 50)}...
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleShowMessageModal(correo.mensaje)}
                          className="p-0 ms-1"
                        >
                          Ver más
                        </Button>
                      </>
                    ) : (
                      correo.mensaje
                    )}
                  </td>
                  <td>
                    <span className={`badge bg-${correo.estado === 'enviado' ? 'success' : correo.estado === 'pendiente' ? 'warning' : 'danger'}`}>
                      {correo.estado}
                    </span>
                  </td>
                  <td className="text-nowrap">
                    {new Date(correo.fecha_envio).toLocaleDateString('es-CL')}
                  </td>
                  <td className="text-center">
                    <ButtonGroup size="sm">
                      <Button
                        variant="outline-danger"
                        onClick={() => dispatch(eliminarCorreo(correo.id))}
                        title="Eliminar"
                      >
                        <i className="bi bi-trash"></i>
                        <span className="d-none d-lg-inline ms-1">Eliminar</span>
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Modal para enviar correo */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-envelope-plus me-2"></i>
            Enviar Correo Masivo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationErrors.general && (
            <Alert variant="danger" dismissible>
              {validationErrors.general}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-3" controlId="asunto">
              <Form.Label>Asunto *</Form.Label>
              <Form.Control
                type="text"
                name="asunto"
                value={correoDetails.asunto}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.asunto}
                placeholder="Asunto del correo"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.asunto}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="mensaje">
              <Form.Label>Mensaje *</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="mensaje"
                value={correoDetails.mensaje}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.mensaje}
                placeholder="Contenido del mensaje"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.mensaje}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="cc">
              <Form.Label>CC</Form.Label>
              <Form.Control
                type="text"
                name="cc"
                value={correoDetails.cc}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.cc}
                placeholder="correo1@sinterec.cl;correo2@sinterec.cl"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.cc}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="bcc">
              <Form.Label>BCC</Form.Label>
              <Form.Control
                type="text"
                name="bcc"
                value={correoDetails.bcc}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.bcc}
                placeholder="correo1@sinterec.cl;correo2@sinterec.cl"
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.bcc}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3" controlId="adjuntos">
              <Form.Label>Adjuntos</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileUpload}
                className="mb-2"
              />
              {correoDetails.adjuntos.length > 0 && (
                <div className="mt-2">
                  <p className="small text-muted">Archivos seleccionados:</p>
                  <ul className="small">
                    {correoDetails.adjuntos.map((file, index) => (
                      <li key={index}>{file.name} ({Math.round(file.size / 1024)} KB)</li>
                    ))}
                  </ul>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseModal} disabled={sending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSendCorreo}
            disabled={sending || !correoDetails.asunto || !correoDetails.mensaje}
          >
            {sending ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                Enviando...
              </>
            ) : (
              <>
                <i className="bi bi-send me-1"></i>
                Enviar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para ver mensaje completo */}
      <Modal show={showMessageModal} onHide={handleCloseMessageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-envelope me-2"></i>
            Mensaje Completo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ whiteSpace: 'pre-line' }}>{selectedMessage}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseMessageModal}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Progress bar para el envío */}
      {sending && (
        <div className="mt-4">
          <h5 className="mb-3">Progreso del envío</h5>
          <ProgressBar 
            animated 
            now={progress} 
            label={`${progress}%`} 
            variant="danger" 
            striped
            style={{ height: '25px' }}
          />
          <p className="text-center mt-2">
            Enviando correos... {progress}% completado
          </p>
        </div>
      )}
    </div>
  );
};

export default CorreoMasivoPage;